"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const QUICK_REASON_KEYS = [
  "offBrand", "wrongFormat", "needsEditing", "lowQuality", "missingHashtags", "wrongPlatformTone",
] as const;

interface RejectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReject: (reason: string) => void;
  shortCode: string;
  isLoading: boolean;
}

export function RejectionModal({ isOpen, onClose, onReject, shortCode, isLoading }: RejectionModalProps) {
  const { t } = useTranslation("pieces");
  const [reason, setReason] = useState("");

  const handleReject = () => {
    if (reason.trim()) {
      onReject(reason.trim());
      setReason("");
    }
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("pieces:rejection.title", { shortCode })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("pieces:rejection.description")}</p>

          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("pieces:rejection.placeholder")}
            className="min-h-[100px]"
            autoFocus
          />

          <div>
            <p className="mb-2 text-xs text-muted-foreground">{t("pieces:rejection.quickReasons")}</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_REASON_KEYS.map((key) => {
                const label = t(`pieces:rejection.quickReasonsList.${key}`);
                return (
                  <Badge
                    key={key}
                    variant="outline"
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => setReason((prev) => (prev ? `${prev}. ${label}` : label))}
                  >
                    {label}
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={isLoading}>{t("common:actions.cancel")}</Button>
          <Button variant="destructive" onClick={handleReject} disabled={!reason.trim() || isLoading}>
            {isLoading ? t("pieces:rejection.rejecting") : t("pieces:rejection.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
